import { ChevronRight, Pencil, Plus, Send, Trash2, Upload, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CsvImportDialog } from "../components/CsvImportDialog";
import { SendIntakeDialog } from "../components/SendIntakeDialog";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { clients as clientsApi, type ClientListItem, type ClientStatus } from "../lib/api";
import { clientInitials } from "../lib/clientName";
import { EmptyState } from "../components/EmptyState";

const STATUS_OPTIONS: { value: ClientStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "completed", label: "Completed" },
  { value: "churned", label: "Churned" },
];

export default function ClientsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ClientStatus | "all">("all");
  const [recordType, setRecordType] = useState<"client" | "lead">("client");
  const [importOpen, setImportOpen] = useState(false);
  const [sendIntake, setSendIntake] = useState<ClientListItem | null>(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const res = await clientsApi.list({
        status: status === "all" ? undefined : status,
        search: search || undefined,
        record_type: recordType,
        limit: 100,
      });
      setItems(res.items);
    } catch (e: any) {
      setError(e?.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, recordType]);

  return (
    <Shell
      title="Clients"
      subtitle="Pipeline"
      actions={
        <>
          <button className="ghost-button" onClick={() => setImportOpen(true)}>
            <Upload size={16} aria-hidden="true" /> Import CSV / Excel
          </button>
          <button className="primary-button" onClick={() => nav("/clients/new")}>
            <Plus size={16} aria-hidden="true" /> New Client
          </button>
        </>
      }
    >
      <div className="panel">
        <div className="filters-row">
          {/* Clients vs Leads. They share a table; without this a lead list buries
              the people whose credit work is actually in progress. */}
          <select
            value={recordType}
            onChange={(e) => setRecordType(e.target.value as "client" | "lead")}
            aria-label="Show clients or leads"
          >
            <option value="client">Clients</option>
            <option value="lead">Leads</option>
          </select>
          <input
            className="search-input"
            placeholder="Search by name, email, phone…"
            aria-label="Search by name, email, or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button className="ghost-button" onClick={load}>Search</button>
        </div>

        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="client-table">
            <div className="client-table-header">
              <span>Client</span>
              <span>Contact</span>
              <span>Status</span>
              <span>Actions</span>
            </div>
            {items.length === 0 && (
              <EmptyState
                icon={<UsersRound size={26} />}
                title="No clients match"
                hint="Try clearing the search or status filter — or add a new client to get started."
              />
            )}
            {items.map((c) => (
              <div key={c.id} className="client-table-row">
                <button className="link-cell" onClick={() => nav(`/clients/${c.id}`)}>
                  <div className="avatar" title={c.legal_name ?? undefined}>{clientInitials(c.legal_name)}</div>
                  <div>
                    <strong>{c.legal_name ?? "(unnamed)"}{(c.unread_messages ?? 0) > 0 && <span className="tab-badge" title={`${c.unread_messages} unread message${c.unread_messages === 1 ? "" : "s"}`}>{c.unread_messages}</span>}</strong>
                    <span>{c.id.slice(0, 8)} · {c.assigned_agent || "Unassigned"}</span>
                  </div>
                </button>
                <div className="contact-cell">
                  <span>{c.email}</span>
                  <span className="muted small">{c.phone}</span>
                </div>
                <StatusPill status={c.status} />
                <div className="row-actions">
                  <button
                    className="icon-button icon-button--sm"
                    onClick={() => setSendIntake(c)}
                    title="Send intake link"
                    aria-label="Send intake link"
                  >
                    <Send size={14} aria-hidden="true" />
                  </button>
                  <button
                    className="icon-button icon-button--sm"
                    onClick={() => nav(`/clients/${c.id}`)}
                    title="Edit"
                    aria-label="Edit"
                  >
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  <button
                    className="icon-button icon-button--sm"
                    title="Delete"
                    aria-label="Delete"
                    style={{ color: "var(--ne-red)" }}
                    onClick={async () => {
                      if (!confirm(`Delete ${c.legal_name ?? "this client"}? This soft-deletes (status → churned) and can be restored.`)) return;
                      try {
                        await clientsApi.remove(c.id);
                        setItems(prev => prev.filter(x => x.id !== c.id));
                      } catch (e: any) {
                        alert(`Delete failed: ${e?.message || "unknown error"}`);
                      }
                    }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                  <button
                    className="icon-button icon-button--sm"
                    onClick={() => nav(`/clients/${c.id}`)}
                    title="Open"
                    aria-label="Open"
                  >
                    <ChevronRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => load()}
      />
      {sendIntake && (
        <SendIntakeDialog
          open={true}
          onClose={() => setSendIntake(null)}
          clientId={sendIntake.id}
          defaultEmail={sendIntake.email}
          defaultPhone={sendIntake.phone}
        />
      )}
    </Shell>
  );
}
